/**
 * Optional sign-in with Google Play Games Services v2 (feature 'playGames'), linked to the game's own account.
 *
 * The game's account (nickname + password, or a guest) stays the identity; a Play Games player is only a second way
 * to reach it on a new device. The client gets a server auth code from the Play Games SDK
 * (GamesSignInClient.requestServerSideAccess with the server's OAuth web client id); the server exchanges it at
 * Google for the Play Games player id (a PlayGamesVerifier, src/server/node/play-games.ts: OAuth code exchange with
 * the client secret, then games/v1/players/me). A code proves the player to this server only: it is single-use and
 * bound to the server's OAuth client.
 *
 *  - auth.linkPlayGames { serverAuthCode }: the signed-in account <-> that Play Games player (C.playGamesLinks by the
 *    Play Games id, C.playGamesPlayers by the account; one batch). A player linked to another account is CONFLICT
 *    'play_games_linked_elsewhere' (unlink there first); linking another Play Games player replaces the old link.
 *  - auth.playGames { serverAuthCode, device }: the linked account gets a new device session (as a sign-in); not
 *    linked: NOT_FOUND 'play_games_not_linked'.
 *  - auth.unlinkPlayGames, auth.playGamesStatus.
 * Locks: 'pgs:<gamesPlayerId>' then 'pgsp:<playerId>'. The account deletion removes both documents.
 */
import type { PlayerId, PlayGamesStatusResult } from '../../../shared/protocol/model';
import { BackendError } from '../../../shared/protocol/errors';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import { C, type ServiceDeps } from './deps';
import type { MonetizationAudit } from './monetization-audit';

/** Exchanges a Play Games server auth code for the Play Games player id (node: Google OAuth + Games API). */
export interface PlayGamesVerifier {
  /** Throws PlayGamesError('invalid') for a code Google refuses, PlayGamesError('unavailable') when Google cannot be asked. */
  playerIdOf(serverAuthCode: string): Promise<string>;
}

export class PlayGamesError extends Error {
  constructor(
    message: string,
    readonly kind: 'invalid' | 'unavailable',
  ) {
    super(message);
    this.name = 'PlayGamesError';
  }
}

export interface PlayGamesLinkRecord {
  playerId: PlayerId;
  linkedAt: number;
}

export interface PlayGamesPlayerRecord {
  gamesPlayerId: string;
  linkedAt: number;
}

export class PlayGamesService {
  constructor(
    private readonly d: ServiceDeps,
    private readonly audit: MonetizationAudit,
    private readonly verifier: PlayGamesVerifier | null,
  ) {}

  get enabled(): boolean {
    return this.verifier !== null;
  }

  async status(playerId: PlayerId): Promise<PlayGamesStatusResult> {
    return { available: this.enabled, linked: (await this.d.storage.get(C.playGamesPlayers, playerId)) !== null };
  }

  /** The Play Games player id of a server auth code (refusals as protocol errors). */
  async verify(serverAuthCode: string): Promise<string> {
    if (!this.verifier) throw new BackendError('FORBIDDEN', 'Play Games sign-in is not configured on this server', { reason: 'feature_disabled', feature: 'playGames' });
    try {
      return await this.verifier.playerIdOf(serverAuthCode);
    } catch (err) {
      if (err instanceof PlayGamesError && err.kind === 'invalid') throw new BackendError('UNAUTHORIZED', 'Google refused the Play Games code', { reason: 'play_games_code_invalid' });
      this.d.logger.warn('play games verification failed', { err });
      throw new BackendError('UNAVAILABLE', 'Google could not be asked right now', { reason: 'play_games_unavailable' });
    }
  }

  /** The account linked to a Play Games player id, or null. */
  async linkedAccount(gamesPlayerId: string): Promise<PlayerId | null> {
    return (await this.d.storage.get<PlayGamesLinkRecord>(C.playGamesLinks, gamesPlayerId))?.playerId ?? null;
  }

  async link(playerId: PlayerId, serverAuthCode: string): Promise<PlayGamesStatusResult> {
    const gamesPlayerId = await this.verify(serverAuthCode);
    return this.d.mutex.run(`pgs:${gamesPlayerId}`, () =>
      this.d.mutex.run(`pgsp:${playerId}`, async () => {
        const holder = await this.d.storage.get<PlayGamesLinkRecord>(C.playGamesLinks, gamesPlayerId);
        if (holder && holder.playerId !== playerId) throw new BackendError('CONFLICT', 'This Play Games player is linked to another account', { reason: 'play_games_linked_elsewhere' });
        const now = this.d.clock.now();
        const ops: StorageBatchOp[] = [];
        const mine = await this.d.storage.get<PlayGamesPlayerRecord>(C.playGamesPlayers, playerId);
        if (mine && mine.gamesPlayerId !== gamesPlayerId) ops.push(deleteOp(C.playGamesLinks, mine.gamesPlayerId));
        ops.push(putOp(C.playGamesLinks, gamesPlayerId, { playerId, linkedAt: now } satisfies PlayGamesLinkRecord));
        ops.push(putOp(C.playGamesPlayers, playerId, { gamesPlayerId, linkedAt: now } satisfies PlayGamesPlayerRecord));
        const { entry, op } = this.audit.prepare({ area: 'playGames', event: 'link', playerId, provider: 'google-play-games', ok: true, details: { replaced: Boolean(mine && mine.gamesPlayerId !== gamesPlayerId) } });
        await this.d.storage.batch([...ops, op]);
        this.audit.stored(entry);
        return { available: true, linked: true };
      }),
    );
  }

  async unlink(playerId: PlayerId): Promise<PlayGamesStatusResult> {
    const mine = await this.d.storage.get<PlayGamesPlayerRecord>(C.playGamesPlayers, playerId);
    if (!mine) return { available: this.enabled, linked: false };
    await this.d.mutex.run(`pgs:${mine.gamesPlayerId}`, () =>
      this.d.mutex.run(`pgsp:${playerId}`, async () => {
        const ops = await this.deletionOps(playerId);
        if (ops.length === 0) return;
        const { entry, op } = this.audit.prepare({ area: 'playGames', event: 'unlink', playerId, provider: 'google-play-games', ok: true, details: {} });
        await this.d.storage.batch([...ops, op]);
        this.audit.stored(entry);
      }),
    );
    return { available: this.enabled, linked: false };
  }

  /** The player's link documents (unlink, account deletion). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    const mine = await this.d.storage.get<PlayGamesPlayerRecord>(C.playGamesPlayers, playerId);
    if (!mine) return [];
    const ops: StorageBatchOp[] = [deleteOp(C.playGamesPlayers, playerId)];
    const link = await this.d.storage.get<PlayGamesLinkRecord>(C.playGamesLinks, mine.gamesPlayerId);
    if (link?.playerId === playerId) ops.push(deleteOp(C.playGamesLinks, mine.gamesPlayerId));
    return ops;
  }
}
